import { registerAttachment } from "../data/mutations.js";
/** OPFS-backed store with an in-memory fallback (private windows, old webviews). */
export function opfsBlobStore() {
    const memory = new Map();
    const dir = async () => {
        if (typeof navigator === "undefined" || !navigator.storage?.getDirectory)
            return null;
        try {
            const root = await navigator.storage.getDirectory();
            return await root.getDirectoryHandle("ect-media", { create: true });
        }
        catch {
            return null;
        }
    };
    return {
        async put(key, blob) {
            const d = await dir();
            if (!d)
                return void memory.set(key, blob);
            const fh = await d.getFileHandle(key, { create: true });
            const w = await fh.createWritable();
            await w.write(blob);
            await w.close();
        },
        async get(key) {
            const d = await dir();
            if (!d)
                return memory.get(key) ?? null;
            try {
                return await (await d.getFileHandle(key)).getFile();
            }
            catch {
                return null;
            }
        },
        async delete(key) {
            const d = await dir();
            if (!d)
                return void memory.delete(key);
            await d.removeEntry(key).catch(() => undefined);
        },
    };
}
/** Browser-side compression (PRD M14-07): longest side ≤ maxPx, JPEG quality tuned for ~300 KB. */
export async function compressImage(file, maxPx = 1600, quality = 0.8) {
    if (typeof createImageBitmap === "undefined")
        return file;
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, maxPx / Math.max(bmp.width, bmp.height));
    const w = Math.round(bmp.width * scale);
    const h = Math.round(bmp.height * scale);
    const canvas = typeof OffscreenCanvas !== "undefined" ? new OffscreenCanvas(w, h) : Object.assign(document.createElement("canvas"), { width: w, height: h });
    const ctx = canvas.getContext("2d");
    if (!ctx)
        return file;
    ctx.drawImage(bmp, 0, 0, w, h);
    bmp.close();
    if ("convertToBlob" in canvas)
        return canvas.convertToBlob({ type: "image/jpeg", quality });
    return new Promise((res) => canvas.toBlob((b) => res(b ?? file), "image/jpeg", quality));
}
/**
 * Media queue: compress → store locally → register metadata (synced as an attachment row) → upload bytes when
 * online → mark uploaded. Thumbnails are produced first so the UI shows the photo immediately.
 */
export class MediaQueue {
    db;
    store;
    uploader;
    actor;
    draining = false;
    constructor(db, store, uploader, actor) {
        this.db = db;
        this.store = store;
        this.uploader = uploader;
        this.actor = actor;
    }
    async enqueue(file, owner) {
        const [full, thumb] = await Promise.all([compressImage(file, 1600, 0.8), compressImage(file, 240, 0.7)]);
        const id = crypto.randomUUID();
        await this.store.put(`${id}.thumb`, thumb);
        await this.store.put(id, full);
        await registerAttachment(this.db, this.actor, { id, ownerTable: owner.table, ownerId: owner.id, kind: owner.kind ?? "photo", mime: "image/jpeg", sizeBytes: full.size, chantierId: owner.chantierId ?? null, entityId: owner.entityId ?? null });
        return { id, thumbUrl: URL.createObjectURL(thumb) };
    }
    async thumbUrl(id) {
        const t = (await this.store.get(`${id}.thumb`)) ?? (await this.store.get(id));
        return t ? URL.createObjectURL(t) : null;
    }
    /** Upload pending bytes; safe to call often (idempotent, single-flight). */
    async drain() {
        if (this.draining)
            return { uploaded: 0, failed: 0 };
        this.draining = true;
        let uploaded = 0;
        let failed = 0;
        try {
            const rows = await this.db.getAll(`select id, mime from attachments where status = 'pending' and uploaded_by = ? order by created_at limit 20`, [this.actor.userId]);
            for (const r of rows) {
                const blob = await this.store.get(r.id);
                if (!blob)
                    continue; // registered on another device or already cleaned up
                try {
                    const target = await this.uploader.uploadUrl(r.id);
                    const res = await this.uploader.put(target.url, { method: target.method, headers: target.headers, body: blob });
                    if (!res.ok)
                        throw new Error(`upload ${res.status}`);
                    await this.db.execute(`update attachments set status = 'uploaded', updated_at = ? where id = ?`, [new Date().toISOString(), r.id]);
                    await this.store.delete(r.id); // keep the thumbnail for the UI
                    uploaded++;
                }
                catch {
                    failed++;
                }
            }
        }
        finally {
            this.draining = false;
        }
        return { uploaded, failed };
    }
}
//# sourceMappingURL=media-queue.js.map